import * as React from 'react';
import { FieldSchemaInfo, Metadata, Schema } from '../../../types/Metadata';
import {
  cn,
  excludeEmptyFilters,
  formatDate,
  getAllViews,
  getSchema,
  parseField
} from '../../../utils';
import './appliedFilters.scss';
import { formatDateFilterValue } from '../DateFilter/dateFilter';
import { Icon } from 'rcd-cpp-ui/ui/icon/Icon';
import { Close } from 'rcd-cpp-ui/ui/icon/Remove';
import { RoundedClose } from '../../Icon/icon';
import { getLabel } from '../Options/options';
import { Op } from '../../../enums/Op';
import { Dropbox } from '../../Dropbox/dropbox';
import { FiltersProps } from '../../../types/FilterProps';
import { View } from '../../../types/View';
import { NodeType } from '../../../enums/NodeType';
import { DataType } from '../../../enums/DataType';
import { useSelector } from '../../../utils/useSelector';
import TooltipPopover from '../../Popover/popover';
import { FieldKey } from '../../../enums/FieldKey';
type RefCache = { [x: string]: Metadata };

const isStagedDataView = (view?: View.Setting) =>
  !!view?.props?.isStagedDataView;

export const AppliedFilters = ({
  setFilter,
  viewRefId,
  filters,
  schemas,
  preappliedFilters,
  getAPI
}: Pick<
  FiltersProps,
  | 'setFilter'
  | 'viewRefId'
  | 'filters'
  | 'schemas'
  | 'preappliedFilters'
  | 'getAPI'
>) => {
  const view = useSelector(s => {
    if (!viewRefId || !s.planning?.selectedWorkspace) return null;
    const allViews = getAllViews(s.planning.selectedWorkspace);
    for (const v of allViews) {
      if (v?.props?.refId == viewRefId) return v;
    }
  });
  const [cache, setCache] = React.useState({});
  const myFilters = excludeEmptyFilters(filters);
  const preAppliedFilters = excludeEmptyFilters(preappliedFilters);
  const isMyExpiryDashBoardModal = useSelector(
    s => s.viewModal?.isMyExpiryDashboard
  );
  const allFilters = []
    .concat(myFilters)
    .concat(preAppliedFilters)
    .filter(x => x);
  const filterCount =
    allFilters.length +
    (view?.isMyItemsView ? 1 : 0) +
    (isStagedDataView(view) ? 1 : 0);
  const { getFieldName, getDescription, getFieldType, nodeType, ids } =
    getReferenceSearchPayload(schemas, allFilters, cache);
  const [expanded, setExpanded] = React.useState(false);
  React.useEffect(() => {
    if (ids.length > 0 && getAPI && expanded) {
      const { searchReferences } = getAPI();
      searchReferences({ nodeType, ids, maxResults: 2 * ids.length })
        .then(results => {
          ids.forEach(id => {
            cache[id] = id;
          });
          return results;
        })
        .then(results => {
          results.forEach(m => {
            cache[m.props.refId] = m;
          });
          setCache({ ...cache });
        });
    }
  }, [ids, expanded]);

  const [viewFilters, parentFilters] = React.useMemo(() => {
    const parentFilters = myFilters?.filter(f => f.isFromParent);
    const viewFilters = myFilters?.filter(f => !f.isFromParent);
    return [viewFilters, parentFilters];
  }, [myFilters]);

  const AppliedFilterBox = ({ setExpanded }) => {
    React.useEffect(() => {
      if (filterCount <= 0) setExpanded(false);
    }, [filterCount]);
    return (
      <div className="applied-filters-container">
        <div className="top-banner">
          <div className="banner-text">Applied Filters ({filterCount})</div>
          <div className="close-container" onClick={() => setExpanded(false)}>
            <Close />
          </div>
        </div>
        <div className="contents">
          <MyItemsPreAppliedFilters view={view} />
          <StagedDataPreAppliedFilters view={view} />
          <FiltersGroup
            label="Preapplied Filters"
            description="These are configured by the Workspace Owner and cannot be changed."
            filters={preAppliedFilters || parentFilters}
            getFieldName={getFieldName}
            getFieldType={getFieldType}
            getDescription={getDescription}
            isMyExpiryDashBoardModal={isMyExpiryDashBoardModal}
          />
          <FiltersGroup
            label="My Filters"
            filters={viewFilters}
            getFieldName={getFieldName}
            getFieldType={getFieldType}
            getDescription={getDescription}
            setFilter={setFilter}
            isMyExpiryDashBoardModal={isMyExpiryDashBoardModal}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="applied-filters-link">
      <Dropbox
        className="filter-dropbox"
        defaultShow={false}
        disabled={!filterCount}
        onStateChanged={setExpanded}
        renderBox={setExpanded => (
          <AppliedFilterBox setExpanded={setExpanded} />
        )}
      >
        <div className="applied-filters">
          Applied Filters {`(${filterCount})`}
        </div>
      </Dropbox>
    </div>
  );
};

const createFilterDisplay = (
  name: string,
  condition: string,
  value: string
) => ({ name, condition, value });
type FilterDisplay = { name?: string; condition: string; value?: string };
const MyItemsPreAppliedFilters = ({ view }: { view: View.Setting }) => {
  if (!view?.isMyItemsView || !view?.props) return null;
  const { isMyExpiryDashboard, isMyTasks, isMyTeam } = view.props;
  let label = 'My Items Filters';
  let filters: FilterDisplay[] = [
    createFilterDisplay('Employee', 'is', 'me'),
    { condition: 'OR' },
    createFilterDisplay('Created By', 'is', 'me')
  ];
  if (isMyExpiryDashboard) {
    label = 'My Expiry Dashboard Filters';
    filters = [
      createFilterDisplay('Lead Author', 'is', 'me'),
      { condition: 'OR' },
      createFilterDisplay('Team Manager', 'is', 'me')
    ];
  } else if (isMyTasks) {
    label = 'My Tasks Filters';
    filters = [createFilterDisplay('Task Owner', 'is', 'me')];
  } else if (isMyTeam) {
    label = 'My Team Filters';
    filters = [createFilterDisplay('Employee Manager', 'is', 'me')];
  }

  const isMyExpiryDashBoardModal = useSelector(
    s => s.viewModal?.isMyExpiryDashboard
  );

  return (
    <FiltersGroup
      label={label}
      isMyExpiryDashBoardModal={isMyExpiryDashBoardModal}
    >
      <span className="filter-subgroup">
        {filters.map(({ name, condition, value }) => (
          <>
            {!!name && <span className="category-name">{name}</span>}
            <span className="condition">{condition}</span>
            {!!value && (
              <span className="condition-values">
                <span className="value-container ">
                  <span className="condition-value disabled">{value}</span>
                </span>
              </span>
            )}
          </>
        ))}
      </span>
    </FiltersGroup>
  );
};

const StagedDataPreAppliedFilters = ({ view }: { view: View.Setting }) => {
  if (!isStagedDataView(view)) return null;
  return (
    <FiltersGroup label="Staged Data Filters">
      <span className="filter-subgroup">
        <span className="category-name">Staged Data</span>
        <span className="condition">is not empty</span>
      </span>
    </FiltersGroup>
  );
};

const getReferenceSearchPayload = (
  schemas: Schema[],
  allFilters: View.FiltersField[],
  cache: RefCache
) => {
  const fieldSchemaInfo: { [x: string]: FieldSchemaInfo } = {};
  const displayFields = {};
  const nodeTypes = new Set<string>();
  const refIds = new Set<string>();
  allFilters.forEach(({ field, conditions }) => {
    let schemaInfo = fieldSchemaInfo[field];
    if (!schemaInfo) {
      schemaInfo = parseField(field, schemas);
      fieldSchemaInfo[field] = schemaInfo;
    }
    if (schemaInfo?.schemaField?.dataType == DataType.enum) return;
    if (!schemaInfo) return;
    const schema = schemaInfo.referenceSchema || schemaInfo.schema;
    if (!schema) return;
    conditions?.forEach(condition => {
      Object.entries(condition).forEach(([op, val]) => {
        if (op == Op.refIds || op == Op.notRefIds) {
          if (schemaInfo?.schemaType == NodeType.SchemalessFilter) {
          } else {
            val?.forEach((refId: string) => {
              displayFields[refId] = schema.props.displayField;
              if (!cache[refId]) {
                nodeTypes.add(schema.props.type);
                refIds.add(refId);
              }
            });
          }
        }
      });
    });
  });
  return {
    getFieldName: (field: string) => {
      const sf = fieldSchemaInfo[field];
      return sf?.schemaField?.displayName || field;
    },
    getDescription: (refId: string, filter: View.FiltersField) => {
      const sf = fieldSchemaInfo[filter?.field];
      if (sf?.schemaField?.dataType == DataType.enum) {
        const enumValues = sf.schemaField.enumValues;
        if (!enumValues) return refId;
        return enumValues[refId] || refId;
      }
      const m = cache[refId];
      if (m?.props?.refId) {
        const labels = m.labels || [];
        const df = displayFields[m.props.refId];
        const desc = m.props[df] || m.props.name || m.props.description;
        if (m.props.itemId) {
          return `${m.props.itemId} - ${desc || '<Title not defined>'}`;
        } else if (labels.includes(NodeType.Conference)) {
          return desc + (m.props.code ? ` (${m.props.code})` : '');
        }
        return desc;
      } else if (schemas?.filter(s => s?.props?.refId == refId)?.length) {
        const schemaName = schemas?.filter(s => s?.props?.refId == refId)[0]
          ?.props?.name;
        return `${!!schemaName ? schemaName : '...'}`;
      } else if (filter?.field != null) {
        switch (filter.field) {
          case FieldKey.ExcludeSecondary:
            const schemaName = getSchema(schemas, refId)?.props?.name;
            return `${!!schemaName ? schemaName : '...'}`;
          case FieldKey.ItemEmployeeRelationType:
            const itemSchema = getSchema(schemas, NodeType.Item);
            const fieldNames = itemSchema?.props?.fields
              ?.map(f => (f?.relationType == refId ? f.name : null))
              .filter(Boolean)
              .join(' / ');
            return fieldNames || refId;
        }
      }
      return '...';
    },
    getFieldType: (field: string) => {
      const sf = fieldSchemaInfo[field];
      return sf?.schemaField?.dataType;
    },
    nodeType: Array.from(nodeTypes)
      .filter(x => x)
      .sort()
      .join('|') as NodeType,
    ids: Array.from(refIds).filter(x => x)
  };
};

type FiltersGroupProps = {
  label: string;
  description?: string;
  filters?: View.FiltersField[];
  getFieldName?: (f: string) => string;
  getDescription?: (refId: string, filter: View.FilterCondition) => string;
  getFieldType?: (f: string) => string;
  children?: any;
  isMyExpiryDashBoardModal?: boolean;
} & Partial<Pick<FiltersProps, 'setFilter'>>;

const FiltersGroup = ({
  label,
  description,
  filters,
  getFieldName,
  getFieldType,
  children,
  isMyExpiryDashBoardModal,
  ...props
}: FiltersGroupProps) => {
  if (!filters?.length && !children) return null;
  return (
    <div className="filters-group">
      <div className="filter-heading">
        {label}
        <span>
          {description && (
            <TooltipPopover
              pointerPosition="right"
              content={description}
              type="blueinfo"
            />
          )}
        </span>
      </div>
      <div className="filter-group">
        {children}
        {filters?.map(f => (
          <span className="filter-subgroup" key={f.field}>
            <span className="category-name">{getFieldName(f.field)}</span>
            {f.conditions.map(condition =>
              Object.entries(condition).map(([op, values]) => (
                <FilterCondition
                  key={`${f.field}_${op}`}
                  {...props}
                  filter={f}
                  op={op}
                  values={values}
                  getFieldType={getFieldType}
                  isMyExpiryDashBoardModal={isMyExpiryDashBoardModal}
                />
              ))
            )}
          </span>
        ))}
      </div>
    </div>
  );
};

type FCProps = {
  filter: View.FiltersField;
  op: string;
  values: any;
  isMyExpiryDashBoardModal?: boolean;
} & Pick<FiltersGroupProps, 'getDescription' | 'setFilter' | 'getFieldType'>;
const FilterCondition = ({
  filter,
  op,
  values,
  getDescription,
  setFilter,
  getFieldType,
  isMyExpiryDashBoardModal
}: FCProps) => {
  let valueContainers = [];
  let removeFilter = (op: string, value?: string) => {
    if (!setFilter) return null;
    return () => {
      const newFilter = { ...filter };
      newFilter.conditions = filter.conditions.reduce<View.FilterCondition[]>(
        (result, condition) => {
          const newCondition = Object.entries(condition).reduce((p, [k, v]) => {
            if (k == op) {
              if (
                op == Op.refIds ||
                op == Op.notRefIds ||
                getFieldType(filter.field) == DataType.enum
              ) {
                if (!Array.isArray(v)) {
                  if (v != value) p[k] = v;
                } else {
                  const nv = v.filter(id => value != id);
                  if (nv.length) p[k] = nv;
                }
              }
            } else {
              p[k] = v;
            }
            return p;
          }, {});
          if (Object.keys(newCondition).length) {
            result.push(newCondition);
          }
          return result;
        },
        []
      );
      setFilter(newFilter);
    };
  };
  switch (op) {
    case Op.refIds:
    case Op.notRefIds:
      if (!Array.isArray(values)) values = [values];
      values.forEach(v => {
        valueContainers.push(
          <ValueContainer
            key={v}
            value={getDescription(v, filter)}
            onRemove={removeFilter(op, v)}
          />
        );
      });
      break;
    case Op.between:
      if (!Array.isArray(values)) values = [values];
      const formattedDates = [];
      values?.forEach(v => {
        if (
          typeof v == 'string' &&
          getFieldType(filter.field) == DataType.date
        ) {
          formattedDates.push(formatDate(v));
        } else {
          formattedDates.push(v);
        }
      });
      valueContainers.push(
        <ValueContainer
          key={op}
          value={formattedDates.join(' to ')}
          onRemove={removeFilter(op)}
        />
      );
      break;
    case Op.relative:
      valueContainers.push(
        <ValueContainer
          key={op}
          value={formatDateFilterValue(values)}
          onRemove={isMyExpiryDashBoardModal ? null : removeFilter(op)}
        />
      );
      break;
    case Op.multiValue:
      valueContainers.push(
        <ValueContainer
          key={op}
          value={values.length == 1 ? `1 value` : `${values.length} values`}
          onRemove={removeFilter(op)}
        />
      );
      break;
    default:
      const formatted = [];
      if (!Array.isArray(values)) values = [values];
      if (
        getFieldType(filter.field) == DataType.enum &&
        op != Op.isEmpty &&
        op != Op.isNotEmpty
      ) {
        values?.forEach(v => {
          const enumFilterDescription: any = getDescription(v, filter);
          valueContainers.push(
            <ValueContainer
              key={op}
              value={
                typeof enumFilterDescription == 'boolean'
                  ? enumFilterDescription.toString()
                  : enumFilterDescription
              }
              onRemove={removeFilter(op, v)}
            />
          );
        });
      } else {
        values?.forEach(v => {
          typeof v == 'string' && getFieldType(filter.field) == DataType.date
            ? formatted.push(formatDate(v))
            : formatted.push(v);
        });
        valueContainers.push(
          <ValueContainer
            key={op}
            value={formatted.toString()}
            onRemove={removeFilter(op)}
          />
        );
      }

      break;
  }
  if (valueContainers.length) {
    return (
      <>
        <span className="condition">{getLabel(op, filter)}</span>
        <span className="condition-values">{valueContainers}</span>
      </>
    );
  }
  return null;
};

const ValueContainer = ({
  onRemove,
  value
}: {
  value: string;
  onRemove?: () => void;
}) => (
  <div className="value-container">
    {value !== null && (
      <span className={cn('condition-value', { disabled: !onRemove })}>
        {value}
      </span>
    )}
    {!!onRemove && (
      <Icon className="x-icon" onClick={onRemove}>
        <RoundedClose />
      </Icon>
    )}
  </div>
);
